import React from "react";
import { Link } from "react-router-dom";
import { trackCTAClick } from "@/lib/analytics";

interface CTASectionProps {
  title: string;
  description: string;
  primaryButtonText?: string;
  primaryButtonHref?: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  showSecondaryButton?: boolean;
  /** Kept so existing callers compile: every variant now renders the design kit's flat card
   *  with the orange band edge (no red or orange fills; GUIDELINES.md §4). */
  variant?: "default" | "dark" | "orange";
  className?: string;
  language?: "en" | "es";
}

const CTASection: React.FC<CTASectionProps> = ({
  title,
  description,
  primaryButtonText,
  primaryButtonHref,
  secondaryButtonText,
  secondaryButtonHref,
  showSecondaryButton = true,
  className = "",
  language = "en",
}) => {
  // Defaults must follow the page language — ES pages otherwise inherit EN labels/routes.
  const es = language === "es";
  primaryButtonText = primaryButtonText ?? (es ? "Agendar llamada de 15 min" : "Set up a 15-min call");
  primaryButtonHref = primaryButtonHref ?? (es ? "/es/agendar" : "/book");
  secondaryButtonText = secondaryButtonText ?? (es ? "Enviar un mensaje" : "Send a Message");
  secondaryButtonHref = secondaryButtonHref ?? (es ? "/es/contacto" : "/contact");
  const isExternalLink = (href: string) => {
    return href.startsWith("http") || href.startsWith("mailto:");
  };

  const handlePrimaryClick = () => {
    trackCTAClick(primaryButtonText, primaryButtonHref, language);
  };

  const handleSecondaryClick = () => {
    trackCTAClick(secondaryButtonText, secondaryButtonHref, language);
  };

  return (
    <section className={`px-4 py-16 lg:py-24 ${className}`}>
      <div className="max-w-4xl p-8 mx-auto text-center card card-band sm:p-12">
        <h2 className="mb-4 text-display-3 text-primary">{title}</h2>
        <p className="max-w-2xl mx-auto mb-8 text-lg text-secondary">{description}</p>
        <div className="flex flex-wrap justify-center gap-3">
          {isExternalLink(primaryButtonHref) ? (
            <a
              href={primaryButtonHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-lg btn-primary"
              onClick={handlePrimaryClick}
              data-mb-tracked="1"
            >
              {primaryButtonText}
            </a>
          ) : (
            <Link
              to={primaryButtonHref}
              className="btn btn-lg btn-primary"
              onClick={handlePrimaryClick}
              data-mb-tracked="1"
            >
              {primaryButtonText}
            </Link>
          )}

          {showSecondaryButton && (
            <>
              {isExternalLink(secondaryButtonHref) ? (
                <a
                  href={secondaryButtonHref}
                  className="btn btn-lg btn-secondary"
                  onClick={handleSecondaryClick}
                  data-mb-tracked="1"
                >
                  {secondaryButtonText}
                </a>
              ) : (
                <Link
                  to={secondaryButtonHref}
                  className="btn btn-lg btn-secondary"
                  onClick={handleSecondaryClick}
                  data-mb-tracked="1"
                >
                  {secondaryButtonText}
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CTASection;
